import { useNavigate } from 'react-router-dom';

export default function DocsPage(): JSX.Element {
  const navigate = useNavigate();

  const handleApiDocsClick = (): void => {
    navigate('/docs/api');
  };

  const handleUserGuidesClick = (): void => {
    // Navigate to user guides when available
    console.log('User guides coming soon');
  };

  const handleBackToHome = (): void => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Page Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm flex-shrink-0">
        <div className="container-max section-padding">
          <div className="flex items-center justify-between py-3">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              
            </h1>
            <div className="flex gap-4">
              <button
                onClick={handleBackToHome}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
              >
                Home
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Documentation Content */}
      <div className="flex-1">
        <div className="container-max section-padding py-12">
          <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
            
            {/* Header */}
            <div className="text-center mb-12 pb-8 border-b-2 border-gray-200 dark:border-gray-700">
              <h1 className="text-4xl font-bold text-primary-600 dark:text-primary-400 mb-4">
                📚 Welcome to Fabrik 28 Documentation
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                Everything you need to integrate with our AI-powered platform
              </p>
            </div>

            {/* Documentation Cards */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              
              {/* API Documentation Card */}
              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="text-4xl mb-4">🔧</div>
                <h3 className="text-2xl font-semibold text-primary-600 dark:text-primary-400 mb-4">
                  API Documentation
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Complete API reference with endpoints, authentication, request/response examples, 
                  and integration guides for developers.
                </p>
                <button
                  onClick={handleApiDocsClick}
                  className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 hover:scale-105"
                >
                  View API Docs
                </button>
              </div>

              {/* User Guides Card */}
              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-8">
                <div className="text-4xl mb-4">📖</div>
                <h3 className="text-2xl font-semibold text-primary-600 dark:text-primary-400 mb-4">
                  User Guides
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Step-by-step tutorials, best practices, and practical examples to help you 
                  get the most out of Fabrik 28's platform.
                </p>
                <button
                  onClick={handleUserGuidesClick}
                  className="inline-block bg-gray-500 text-white font-medium py-3 px-6 rounded-lg cursor-not-allowed"
                  disabled
                >
                  Coming Soon
                </button>
              </div>
            </div>

            {/* What You'll Find Here */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-8 mb-8">
              <h3 className="text-xl font-semibold text-blue-800 dark:text-blue-200 mb-4">
                🎯 What You'll Find Here
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li><strong>API Reference:</strong> Detailed documentation of all available endpoints</li>
                <li><strong>Authentication:</strong> How to securely connect to our services</li>
                <li><strong>Code Examples:</strong> Ready-to-use code snippets in multiple languages</li>
                <li><strong>Best Practices:</strong> Tips for optimal integration and performance</li>
                <li><strong>Troubleshooting:</strong> Common issues and their solutions</li>
                <li><strong>Rate Limits:</strong> Understanding usage limits and optimization</li>
              </ul>
            </div>

            {/* Need Help Section */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg p-8 text-center mb-8">
              <h2 className="text-2xl font-semibold mb-4">💡 Need Help?</h2>
              <p className="text-lg mb-6 opacity-90">
                Our team is here to support your integration journey. Whether you have technical questions 
                or need guidance on best practices, we're ready to help.
              </p>
              <a
                href="mailto:support@fabrik28.ai"
                className="inline-block bg-white text-primary-600 font-medium py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors duration-200 border-2 border-white hover:border-gray-100"
              >
                📧 Contact Support
              </a>
            </div>

            {/* Documentation Updates */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">
                📋 Documentation Updates
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                We regularly update our documentation to include new features, improvements, and community feedback. 
                Check back regularly for the latest information.
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
